import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  computed,
  inject,
  signal
} from '@angular/core';
import { CommonModule, CurrencyPipe, DecimalPipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ChartModule } from 'primeng/chart';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { getForecastRange, type ForecastHorizon, type ForecastPeriod } from '../../services/forecast-cash-flow.service';
import {
  ForecastChurnService,
  type ChurnForecastData,
  type ChurnRiskLevel,
  type ChurnSubscriptionRow
} from '../../services/forecast-churn.service';

@Component({
  selector: 'app-forecast-churn',
  templateUrl: './forecast-churn.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    CurrencyPipe,
    DecimalPipe,
    ChartModule,
    TableModule,
    TagModule,
    TooltipModule
  ],
  styles: [
    `
      :host ::ng-deep .p-chart {
        display: block;
        width: 100%;
        height: 100%;
      }
      :host ::ng-deep .p-chart canvas {
        max-width: 100% !important;
        max-height: 100% !important;
      }
      ::ng-deep .forecast-help-tooltip.p-tooltip,
      ::ng-deep .p-tooltip.forecast-help-tooltip {
        max-width: 22rem;
      }
      ::ng-deep .forecast-help-tooltip .p-tooltip-text,
      ::ng-deep .p-tooltip.forecast-help-tooltip .p-tooltip-text {
        white-space: pre-line;
        line-height: 1.45;
        font-size: 0.75rem;
        text-align: left;
      }
    `
  ]
})
export class ForecastChurnComponent implements OnInit {
  private churnService = inject(ForecastChurnService);

  readonly help = {
    healthy:
      '¿Cuánto MRR está sano?\n\n' +
      'Suma el ingreso mensual equivalente de suscripciones ACTIVE y en prueba (TRAILING).\n\n' +
      'Es la base recurrente que hoy no tiene atraso.',

    atRisk:
      '¿Cuánto MRR puede perderse?\n\n' +
      'Suscripciones en PAST_DUE: el cliente no pagó la renovación y está en cobranza.\n\n' +
      'Si no se recupera, ese MRR se convierte en churn.',

    churned:
      '¿Cuánto MRR se perdió en el periodo?\n\n' +
      'Suscripciones canceladas o finalizadas cuya fecha de salida cae en el rango seleccionado.\n\n' +
      'Se muestra en equivalente mensual (MRR).',

    retention:
      '¿Qué tan bien retienes clientes e ingresos?\n\n' +
      'Retención de logos: (activas + en riesgo) / (esas + canceladas del periodo).\n' +
      'Retención de ingresos: misma idea, pero en MRR.\n\n' +
      'Más alto = mejor. 100% significa que no hubo bajas en el periodo.',

    status:
      '¿Cómo está repartida tu cartera?\n\n' +
      'Cuenta y MRR por estado: activas, prueba, atrasadas, canceladas y finalizadas.',

    timeline:
      '¿Cómo se ve la cartera mes a mes?\n\n' +
      'Barras verdes: renovaciones de suscripciones activas/prueba.\n' +
      'Barras ámbar: renovaciones atrasadas (PAST_DUE).\n' +
      'Barras rojas: MRR cancelado o finalizado ese mes.\n\n' +
      'Así comparas lo sano vs lo en riesgo vs lo perdido.',

    frequency:
      '¿Qué ciclos concentran el riesgo y el churn?\n\n' +
      'Agrupa suscripciones en riesgo + dadas de baja por frecuencia (mensual, anual, etc.).',

    atRiskTable:
      'Lista de suscripciones atrasadas (PAST_DUE).\n\n' +
      'Ordénalas por MRR para priorizar recuperación de los clientes más valiosos.',

    churnedTable:
      'Lista de bajas del periodo (canceladas / finalizadas).\n\n' +
      'Sirve para analizar por qué se fueron y cuánto MRR se llevó cada una.',

    renewals:
      'Renovaciones de los próximos 30 días.\n\n' +
      'Te adelanta qué cobros recurrentes vienen y cuáles ya van atrasados.'
  } as const;

  /** Glosario del pie de página. */
  readonly glossary: { term: string; definition: string }[] = [
    {
      term: 'Periodo',
      definition:
        'Rango de fechas del análisis (mes en curso, 3/6/12 meses o personalizado). Las bajas (churn) se cuentan solo si salieron dentro de ese rango.'
    },
    {
      term: 'MRR',
      definition:
        'Monthly Recurring Revenue: ingreso recurrente convertido a equivalente mensual (p. ej. una anualidad de $12,000 = $1,000 de MRR). No es el cobro de un mes concreto.'
    },
    {
      term: 'MRR sano',
      definition:
        'MRR de suscripciones ACTIVE (activas) y TRAILING (en prueba). Es la base recurrente que hoy no tiene atraso de pago.'
    },
    {
      term: 'MRR en riesgo',
      definition:
        'MRR de suscripciones PAST_DUE (atrasadas): el cliente no pagó la renovación. Si no se recupera, ese ingreso puede convertirse en churn.'
    },
    {
      term: 'MRR churned',
      definition:
        'MRR perdido por suscripciones canceladas (CANCELED) o finalizadas (FINISHED) cuya fecha de salida cae en el periodo.'
    },
    {
      term: 'Churn',
      definition:
        'Pérdida de suscripciones (bajas). En esta pantalla se mide en cantidad de suscripciones y en MRR que dejaron de generar.'
    },
    {
      term: 'Retención de logos',
      definition:
        'Porcentaje de suscripciones que se mantienen (activas + en riesgo) frente a esas más las bajas del periodo. Mide retención de clientes, no de dinero.'
    },
    {
      term: 'Retención de ingresos',
      definition:
        'Misma idea que la retención de logos, pero en MRR: cuánto ingreso recurrente conservas frente al que se perdió en el periodo.'
    },
    {
      term: '% en riesgo',
      definition:
        'Qué parte del MRR vivo (sano + en riesgo) está atrasado. Más alto = mayor presión de cobranza sobre la cartera recurrente.'
    },
    {
      term: 'ACTIVE',
      definition:
        'Suscripción activa y al corriente: genera cobros recurrentes según su frecuencia.'
    },
    {
      term: 'TRAILING',
      definition:
        'Suscripción en periodo de prueba. Cuenta como cartera sana, pero aún no es un cliente plenamente consolidado.'
    },
    {
      term: 'PAST_DUE',
      definition:
        'Suscripción atrasada: falló o no se completó el cobro de la renovación. Es la señal principal de riesgo de churn.'
    },
    {
      term: 'CANCELED / FINISHED',
      definition:
        'Cancelada = se dio de baja. Finalizada = llegó a su fecha de fin. Ambas dejan de aportar MRR y cuentan como churn del periodo si salieron en el rango.'
    },
    {
      term: 'Frecuencia',
      definition:
        'Cada cuánto se renueva la suscripción (mensual, trimestral, anual, etc.). Se usa para convertir el monto a MRR y proyectar renovaciones en el tiempo.'
    },
    {
      term: 'Renovaciones (30 días)',
      definition:
        'Suscripciones vivas con próxima fecha de cobro en los siguientes 30 días. Sirve para anticipar cobros y detectar atrasos cercanos.'
    }
  ];

  data = signal<ChurnForecastData | null>(null);
  period = signal<ForecastPeriod>({ horizon: '6m' });
  customDateFrom = signal('');
  customDateTo = signal('');
  tableTab = signal<'risk' | 'churned' | 'renewals'>('risk');
  glossaryOpen = signal(false);

  toggleGlossary(): void {
    this.glossaryOpen.update((v) => !v);
  }

  readonly horizonOptions: { value: ForecastHorizon; label: string }[] = [
    { value: 'month', label: 'Mes en curso' },
    { value: '3m', label: '3 meses' },
    { value: '6m', label: '6 meses' },
    { value: '12m', label: '12 meses' },
    { value: 'custom', label: 'Personalizado' }
  ];

  readonly periodLabel = computed(() => {
    const { from, to } = getForecastRange(this.period());
    const fmt = (d: Date) =>
      d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${fmt(from)} – ${fmt(to)}`;
  });

  readonly todayLabel = computed(() =>
    new Date().toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  );

  private readonly statusColors: Record<string, string> = {
    ACTIVE: 'rgba(16, 185, 129, 0.9)',
    TRAILING: 'rgba(14, 165, 233, 0.9)',
    PAST_DUE: 'rgba(245, 158, 11, 0.95)',
    CANCELED: 'rgba(244, 63, 94, 0.9)',
    FINISHED: 'rgba(100, 116, 139, 0.85)',
    PENDING_PAYMENT: 'rgba(148, 163, 184, 0.9)'
  };

  readonly statusChart = computed(() => {
    const rows = this.data()?.byStatus ?? [];
    return {
      labels: rows.map((r) => this.statusLabel(r.status)),
      datasets: [
        {
          data: rows.map((r) => r.mrr),
          backgroundColor: rows.map((r) => this.statusColors[r.status] || 'rgba(148,163,184,0.8)'),
          borderWidth: 0,
          hoverOffset: 2
        }
      ]
    };
  });

  readonly statusLegend = computed(() =>
    (this.data()?.byStatus ?? []).map((r) => ({
      label: this.statusLabel(r.status),
      count: r.count,
      mrr: r.mrr,
      color: this.statusColors[r.status] || 'rgba(148,163,184,0.8)'
    }))
  );

  readonly timelineChart = computed(() => {
    const months = this.data()?.months ?? [];
    return {
      labels: months.map((m) => m.label),
      datasets: [
        {
          type: 'bar' as const,
          label: 'MRR activas',
          backgroundColor: 'rgba(16, 185, 129, 0.85)',
          data: months.map((m) => m.activeMrr),
          borderRadius: 4,
          stack: 'a'
        },
        {
          type: 'bar' as const,
          label: 'MRR en riesgo',
          backgroundColor: 'rgba(245, 158, 11, 0.85)',
          data: months.map((m) => m.atRiskMrr),
          borderRadius: 4,
          stack: 'a'
        },
        {
          type: 'bar' as const,
          label: 'MRR churned',
          backgroundColor: 'rgba(244, 63, 94, 0.85)',
          data: months.map((m) => m.churnedMrr),
          borderRadius: 4,
          stack: 'a'
        }
      ]
    };
  });

  readonly timelineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' as const, labels: { boxWidth: 12, font: { size: 11 } } },
      tooltip: {
        callbacks: {
          label: (ctx: { dataset: { label?: string }; parsed: { y: number | null } }) => {
            const v = ctx.parsed.y ?? 0;
            return `${ctx.dataset.label}: ${this.formatMxn(v)}`;
          }
        }
      }
    },
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { color: '#64748b', font: { size: 11 } } },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: 'rgba(15, 23, 42, 0.06)' },
        ticks: {
          color: '#64748b',
          font: { size: 11 },
          callback: (value: string | number) => this.formatMxn(Number(value))
        }
      }
    }
  };

  readonly doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '62%',
    layout: { padding: 0 },
    plugins: { legend: { display: false } }
  };

  readonly tableRows = computed(() => {
    const d = this.data();
    if (!d) return [] as ChurnSubscriptionRow[];
    switch (this.tableTab()) {
      case 'churned':
        return d.churned;
      case 'renewals':
        return d.upcomingRenewals;
      default:
        return d.atRisk;
    }
  });

  ngOnInit(): void {
    this.load();
  }

  setHorizon(horizon: ForecastHorizon): void {
    if (horizon === 'custom') {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      this.customDateFrom.set(`${y}-${m}-${String(now.getDate()).padStart(2, '0')}`);
      const end = new Date(y, now.getMonth() + 6, 0);
      this.customDateTo.set(
        `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`
      );
      this.period.set({ horizon: 'custom' });
      return;
    }
    this.period.set({ horizon });
    this.load();
  }

  applyCustomPeriod(): void {
    const from = this.customDateFrom();
    const to = this.customDateTo();
    if (!from || !to) return;
    this.period.set({ horizon: 'custom', dateFrom: from, dateTo: to });
    this.load();
  }

  setTableTab(tab: 'risk' | 'churned' | 'renewals'): void {
    this.tableTab.set(tab);
  }

  load(): void {
    this.data.update((d) =>
      d
        ? { ...d, loading: true, error: undefined }
        : {
            from: '',
            to: '',
            kpis: {
              healthyMrr: 0,
              healthyCount: 0,
              atRiskMrr: 0,
              atRiskCount: 0,
              churnedMrr: 0,
              churnedCount: 0,
              logoRetentionRate: 100,
              revenueRetentionRate: 100,
              atRiskRate: 0,
              upcomingAtRiskCount: 0,
              upcomingAtRiskMrr: 0
            },
            byStatus: [],
            byFrequency: [],
            months: [],
            atRisk: [],
            churned: [],
            upcomingRenewals: [],
            loading: true
          }
    );
    this.churnService.load(this.period()).subscribe((d) => this.data.set(d));
  }

  formatMxn(amount: number): string {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  }

  formatDay(yyyyMmDd: string | undefined): string {
    if (!yyyyMmDd || yyyyMmDd.length < 10) return '—';
    const [y, m, d] = yyyyMmDd.slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
  }

  statusLabel(status: string): string {
    const map: Record<string, string> = {
      ACTIVE: 'Activa',
      TRAILING: 'Prueba',
      PAST_DUE: 'Atrasada',
      CANCELED: 'Cancelada',
      CANCELLED: 'Cancelada',
      FINISHED: 'Finalizada',
      PENDING_PAYMENT: 'Pendiente pago'
    };
    return map[status] || status;
  }

  statusSeverity(
    status: string
  ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'TRAILING':
        return 'info';
      case 'PAST_DUE':
        return 'warn';
      case 'CANCELED':
      case 'CANCELLED':
        return 'danger';
      case 'FINISHED':
        return 'secondary';
      default:
        return 'contrast';
    }
  }

  riskSeverity(risk: ChurnRiskLevel): 'success' | 'warn' | 'danger' {
    if (risk === 'HEALTHY') return 'success';
    if (risk === 'WATCH') return 'warn';
    return 'danger';
  }

  riskLabel(risk: ChurnRiskLevel): string {
    if (risk === 'HEALTHY') return 'Sana';
    if (risk === 'WATCH') return 'Vigilancia';
    return 'Crítica';
  }

  frequencyLabel(freq: string | undefined): string {
    if (!freq) return '—';
    const map: Record<string, string> = {
      DAILY: 'Diaria',
      WEEKLY: 'Semanal',
      BIWEEKLY: 'Quincenal',
      MONTHLY: 'Mensual',
      QUARTERLY: 'Trimestral',
      SEMIANNUALLY: 'Semestral',
      ANNUALLY: 'Anual',
      ONCE: 'Única'
    };
    return map[freq] || freq;
  }

  methodLabel(method: string | undefined): string {
    if (!method) return '—';
    const map: Record<string, string> = {
      LINK: 'Liga de pago',
      TRANSFER: 'Transferencia',
      MANUAL: 'Manual',
      RECURRING_CHARGE: 'Cargo recurrente'
    };
    return map[method] || method;
  }
}
